import React from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import useOptionListUsages from "Catalog/Edit/shared/hooks/useOptionListUsages"
import { calculateUpdatedPrice, useBulkUpdatePrices } from "Catalog/Grid/shared/components/BulkUpdatePrices"
import { useGenerateRefs } from "Catalog/Grid/shared/components/GenerateRefs"
import GridLayout from "Catalog/Grid/shared/components/GridLayout"
import GridTable from "Catalog/Grid/shared/components/GridTable"
import ManageVariants from "Catalog/Grid/shared/components/ManageVariants"
import CatalogToolbar from "Catalog/shared/components/CatalogToolbar"
import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import useCatalogNavigation from "Catalog/shared/hooks/useCatalogNavigation"
import useFindItemOrRedirect from "Catalog/shared/hooks/useFindItemOrRedirect"
import useKeyboardNavigation from "Catalog/shared/hooks/useKeyboardNavigation"
import useEditor from "shared/hooks/useEditor"
import { IOption, IOptionList, IVariant } from "shared/hubrise/models"
import * as Option from "shared/hubrise/utils/Option"
import * as Restrictions from "shared/hubrise/utils/Restrictions"

type Item = { id: number; optionList: IOptionList; option: IOption }

const Options = (): JSX.Element => {
  const { t } = useTranslation()
  const params = useParams<{ optionId: string }>()

  const { editor, catalog } = useEditor()
  const optionListUsages = useOptionListUsages("short")
  const { remount } = useStatefulFieldRemount()

  const { catalogNavigation, visit } = useCatalogNavigation()
  const indexPath = catalogNavigation.gridPath("options")

  const toolbar = <CatalogToolbar commandActions={<ManageVariants />} />

  const items: Array<Item> = React.useMemo(
    () => Option.allOptions(catalog).map(({ optionList, option }) => ({ id: option.id, optionList, option })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, editor.catalogHash],
  )

  const selectedItem = useFindItemOrRedirect<Item>(
    params.optionId,
    (id) => items.find((item) => item.id === id),
    indexPath,
  )

  const removeAllRefs = () => {
    editor.option.removeAllRefs()
    remount()
  }

  const { generateRefsModal, openGenerateRefs } = useGenerateRefs("option", (options) => {
    editor.option.generateRefs(options)
    remount()
  })

  const enableAll = (enabled: boolean, variantRef?: string) => {
    editor.option.enableAll(enabled, variantRef)
    remount()
  }

  const { bulkUpdatePricesModal, openBulkUpdatePrices } = useBulkUpdatePrices("options", (variant, rules) => {
    editor.option.bulkUpdatePrices(
      rules.sourceVariantRef,
      variant?.ref ?? null,
      rules.skipAlreadySetPrices,
      rules.optionListNames,
      (price) => calculateUpdatedPrice(price, rules),
    )
    remount()
  })

  const clearAllPrices = (variant: IVariant) => {
    editor.option.clearAllPrices(variant.ref)
    remount()
  }

  React.useEffect(() => {
    visit("option", selectedItem?.option)
  }, [selectedItem, visit])

  useKeyboardNavigation<Item>({
    item: selectedItem,
    collection: items,
    indexPath: () => indexPath,
    showPath: (item) => catalogNavigation.gridPath("option", item.option.id),
  })

  const main =
    items.length > 0 ? (
      <>
        {bulkUpdatePricesModal}
        {generateRefsModal}

        <GridTable
          items={items}
          selectedItem={selectedItem}
          designatorsHeaders={[t("grid.option_list"), t("grid.used_in"), t("grid.option")]}
          removeAllRefs={removeAllRefs}
          openGenerateRefs={openGenerateRefs}
          variants={catalog.data.variants}
          enableAll={enableAll}
          openBulkUpdatePrices={openBulkUpdatePrices}
          clearAllPrices={clearAllPrices}
          selectItemPath={(item) => catalogNavigation.gridPath("option", item.option.id)}
          editItemPath={(item) => catalogNavigation.editPath("option_list", item.optionList.id)}
          previewItemPath={undefined}
          designators={(item) => [item.optionList.name, optionListUsages(item.optionList), item.option.name]}
          itemRef={(item) => item.option.ref}
          setRef={(item, ref) => editor.option.update(item.optionList.id, item.option.id, "ref", ref)}
          catalogPrice={(item) => item.option.price}
          setCatalogPrice={(item, price) => editor.option.update(item.optionList.id, item.option.id, "price", price)}
          variantPrice={(item, variantRef) => Option.variantPrice(item.option, variantRef)}
          setVariantPrice={(item, variantRef, price) =>
            editor.option.updateVariantPrice(item.optionList.id, item.option.id, variantRef, price)
          }
          isEnabled={(item, variantRef) => Restrictions.isEnabled(item.option.restrictions, variantRef)}
          enable={(item, enabled, variantRef) =>
            editor.option.enable(item.optionList.id, item.option.id, enabled, variantRef)
          }
        />
      </>
    ) : null

  return <GridLayout toolbar={toolbar} main={main} none={t("option_list.none")} testId="grid-options" />
}

export default Options
